import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { updateHandlersMap } from '../../utils/updateHandlersMap';
import './InputRecommend.css';

export interface InputRecommendNEWProps {
  /** 字段类型：区分场景字段和拍摄地点字段 */
  fieldType: 'sceneSet' | 'sceneLocation';
  /** 场景ID */
  sceneId: string;
  /** 数据源：当前分场下所有场次的历史值 */
  dataSource: Array<string | undefined | null>;
  /** 使用场景：'table' 用于场次表，'detail' 用于场次详情 */
  variant?: 'table' | 'detail';
  /** 用于自动填充地点的回调（可选，仅用于 sceneSet） */
  onAutoFillLocation?: (sceneId: string, sceneSetValue: string) => void;
  /** 行索引（用于表格场景） */
  rowIndex?: number;
  /** 完成编辑的回调（可选，用于表格场景，选择推荐项后调用） */
  onFinishEdit?: () => void;
  /** 保存值到 ref 的回调（可选，用于表格场景，选择推荐项后立即保存值） */
  onSaveValue?: (value: string) => void;
  /** 表格模式：显式绑定输入框；传入时禁用 querySelector + setInterval 查找 */
  targetInputRef?: React.RefObject<HTMLInputElement | null>;
  /** 选中推荐项时优先调用，用于受控 input 与 TableCell 同一更新路径（避免仅改 DOM） */
  onSelectValue?: (text: string) => void;
}

function normalize(str?: string | null): string | null {
  if (str === null || str === undefined) return null;
  const trimmed = String(str).trim();
  return trimmed.length ? trimmed : null;
}

// 构建搜索结果：按名称排序，显示全部结果
function buildResults(dataSource: Array<string | undefined | null>, keyword?: string): string[] {
  const freq = new Map<string, number>();
  const kw = normalize(keyword)?.toLowerCase();
  for (const item of dataSource) {
    const n = normalize(item);
    if (!n) continue;
    // 排除"待定-"前缀的值
    if (n.startsWith('暂时去掉待定-')) continue;
    // 如果有关键词，进行过滤；如果关键词为空，显示所有结果
    if (kw && !n.toLowerCase().includes(kw)) continue;
    freq.set(n, (freq.get(n) || 0) + 1);
  }
  // 按名称排序（支持英文和汉字拼音）
  const arr = Array.from(freq.keys())
    .sort((a, b) => a.localeCompare(b));
  return arr;
}

const InputRecommendNEW: React.FC<InputRecommendNEWProps> = ({
  fieldType,
  sceneId,
  dataSource,
  variant = 'table',
  onAutoFillLocation,
  rowIndex,
  onFinishEdit,
  onSaveValue,
  targetInputRef,
  onSelectValue,
}) => {
  // 状态管理
  const [searchText, setSearchText] = useState<string>('');
  const [visible, setVisible] = useState<boolean>(false);
  const [inputElementSet, setInputElementSet] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  
  const inputRefInternal = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement[]>([]);
  const [menuPosition, setMenuPosition] = useState<{ top?: number; bottom?: number; left: number; width: number; openUpward?: boolean; maxHeight?: number } | null>(null);
  // 标记是否是用户主动选择推荐项，用于避免 MutationObserver 重复调用 onAutoFillLocation
  const isUserSelectionRef = useRef<boolean>(false);
  // 保存菜单的打开方向，滚动时保持不变
  const menuOpenDirectionRef = useRef<boolean | null>(null);

  /** 表格模式 + 显式 ref：绑定 focus/blur/input，不使用轮询 */
  useLayoutEffect(() => {
    if (variant !== 'table' || !targetInputRef) return;

    let cancelled = false;
    let el = targetInputRef.current;

    const attach = (inputEl: HTMLInputElement) => {
      inputRefInternal.current = inputEl;
      setInputElementSet(true);

      const onFocusIn = () => {
        setVisible(true);
        setSearchText(inputEl.value || '');
      };
      const onBlurOut = () => {
        setTimeout(() => {
          if (cancelled) return;
          if (document.activeElement !== inputEl) {
            setVisible(false);
          }
        }, 150);
      };
      const onInputEv = () => {
        setSearchText(inputEl.value || '');
      };

      inputEl.addEventListener('focus', onFocusIn);
      inputEl.addEventListener('blur', onBlurOut);
      inputEl.addEventListener('input', onInputEv);

      if (document.activeElement === inputEl) {
        setVisible(true);
        setSearchText(inputEl.value || '');
      }

      return () => {
        inputEl.removeEventListener('focus', onFocusIn);
        inputEl.removeEventListener('blur', onBlurOut);
        inputEl.removeEventListener('input', onInputEv);
      };
    };

    let detach: (() => void) | undefined;
    if (el) {
      detach = attach(el);
    } else {
      const raf = requestAnimationFrame(() => {
        if (cancelled) return;
        el = targetInputRef.current;
        if (el) {
          detach = attach(el);
        }
      });
      return () => {
        cancelled = true;
        cancelAnimationFrame(raf);
        detach?.();
        inputRefInternal.current = null;
        setInputElementSet(false);
      };
    }

    return () => {
      cancelled = true;
      detach?.();
      inputRefInternal.current = null;
      setInputElementSet(false);
    };
  }, [variant, targetInputRef, sceneId, fieldType]);

  // 表格模式（无显式 ref）：通过 DOM 查询找到输入框元素，并监听 focus/blur 事件
  useEffect(() => {
    if (variant === 'table' && targetInputRef) {
      return;
    }

    const findAndSetupInput = () => {
      const root =
        variant === 'detail'
          ? document.querySelector('.scene-detail-container')
          : document.querySelector('.breakdown-table-container');
      if (!root) return;

      const inputElement = root.querySelector(
        `input[data-scene-id="${sceneId}"][data-field="${fieldType}"]`
      ) as HTMLInputElement | null;
      
      if (inputElement && inputElement !== inputRefInternal.current) {
        inputRefInternal.current = inputElement;
        setInputElementSet(true);
        
        // 检查是否聚焦（聚焦时显示菜单）
        const isFocused = document.activeElement === inputElement;
        setVisible(isFocused);
        
        if (isFocused) {
          setSearchText(inputElement.value || '');
        }
      }
    };
    
    // 立即查找一次
    findAndSetupInput();
    
    // 定期检查（用于处理动态渲染的情况）
    const intervalId = setInterval(findAndSetupInput, 100);
    
    // 监听全局 focus 事件
    const handleGlobalFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target && target.getAttribute('data-scene-id') === sceneId && 
          target.getAttribute('data-field') === fieldType) {
        inputRefInternal.current = target as HTMLInputElement;
        setInputElementSet(true);
        setVisible(true);
        setSearchText((target as HTMLInputElement).value || '');
      }
    };
    
    // 监听全局 blur 事件
    const handleGlobalBlur = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target && target.getAttribute('data-scene-id') === sceneId && 
          target.getAttribute('data-field') === fieldType) {
        // 延迟关闭，给选择推荐项的时间
        setTimeout(() => {
          if (document.activeElement !== target) {
            setVisible(false);
          }
        }, 150);
      }
    };
    
    document.addEventListener('focus', handleGlobalFocus, true);
    document.addEventListener('blur', handleGlobalBlur, true);
    
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('focus', handleGlobalFocus, true);
      document.removeEventListener('blur', handleGlobalBlur, true);
    };
  }, [sceneId, fieldType, variant, targetInputRef]);

  // 监听输入框的 input 事件，更新搜索文本（显式 ref 模式下已在 useLayoutEffect 中绑定）
  useEffect(() => {
    if (variant === 'table' && targetInputRef) return;
    if (!inputRefInternal.current || !inputElementSet) return;

    const inputElement = inputRefInternal.current;
    
    const handleInput = (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target === inputElement) {
        setSearchText(target.value || '');
      }
    };

    inputElement.addEventListener('input', handleInput);
    
    return () => {
      inputElement.removeEventListener('input', handleInput);
    };
  }, [inputElementSet, variant, targetInputRef]);

  // 计算表格模式下搜索结果菜单的位置
  const updateMenuPosition = useCallback((forceRecalculateDirection: boolean = false) => {
    if (variant !== 'table' || !inputRefInternal.current || !visible) return;
    
    const inputElement = inputRefInternal.current;
    const tableContainer = document.querySelector('.breakdown-table-container') as HTMLElement;
    
    if (!inputElement || !tableContainer) return;
    
    // 检查元素是否仍然连接在 DOM 中
    if (!inputElement.isConnected || !tableContainer.isConnected) return;
    
    // 获取输入框和容器的位置信息
    const inputRect = inputElement.getBoundingClientRect();
    const containerRect = tableContainer.getBoundingClientRect();
    
    // 查找单元格元素，使用单元格的宽度而不是输入框的宽度
    const cellElement = inputElement.closest('.excel-table-cell') as HTMLElement;
    let cellWidth = inputRect.width;
    let cellLeft = inputRect.left;
    
    if (cellElement) {
      const cellRect = cellElement.getBoundingClientRect();
      cellWidth = cellRect.width;
      cellLeft = cellRect.left;
    }
    
    // 计算输入框相对于容器内容区域的位置
    // getBoundingClientRect() 返回的是相对于视口的坐标
    // position: absolute 相对于 position: relative 的容器时，top/bottom 是相对于容器内容区域的
    // 所以需要：输入框相对于视口的位置 - 容器相对于视口的位置 + 容器的 scrollTop
    const containerScrollTop = tableContainer.scrollTop;
    const relativeTop = inputRect.top - containerRect.top + containerScrollTop;
    const relativeBottom = inputRect.bottom - containerRect.top + containerScrollTop;
    const relativeLeft = cellLeft - containerRect.left;
    
    // 验证：relativeTop 应该是输入框顶部相对于容器内容区域顶部的位置
    // 如果容器没有滚动，relativeTop = inputRect.top - containerRect.top（输入框到容器可见区域顶部的距离）
    // 如果容器滚动了 scrollTop，relativeTop = (inputRect.top - containerRect.top) + scrollTop
    // 这应该等于输入框在内容区域中的实际位置
    
    // 计算可用空间和容器高度
    const containerHeight = containerRect.height;
    // 向上可用空间 = 输入框顶部到容器顶部的距离（相对于视口，用于判断方向）
    const availableSpaceAbove = inputRect.top - containerRect.top;
    // 向下可用空间 = 容器底部到输入框底部的距离（相对于视口，用于判断方向）
    const availableSpaceBelow = containerRect.bottom - inputRect.bottom;
    
    // 获取表头高度（表头是 sticky 定位，固定在容器顶部）
    const excelTableContainer = tableContainer.querySelector('.excel-table-container');
    const headerElement = excelTableContainer?.querySelector('thead') as HTMLElement;
    const headerHeight = headerElement ? headerElement.offsetHeight : 40; // 默认 40px
    
    // 注意：relativeTop 是输入框顶部相对于容器内容区域顶部的位置
    // 但表头是 sticky 的，固定在容器顶部，所以实际可用的向上空间需要减去表头高度
    // 实际可用空间 = relativeTop - headerHeight
    
    // 最大高度限制：容器高度的 75%
    const maxMenuHeight = containerHeight * 0.75;
    
    // 智能判断打开方向
    let shouldOpenUpward: boolean;
    if (forceRecalculateDirection || menuOpenDirectionRef.current === null) {
      // 向上打开时需要考虑溢出问题（防止溢出容器顶部）
      // 向下打开时允许溢出（用户可以滚动查看）
      // 默认向下打开，只有在以下情况才向上打开：
      // 1. 向下空间很小（< 100px）且向上空间明显更大（至少是向下空间的 2 倍）
      // 2. 向上空间足够大（>= 300px）且明显大于向下空间（至少是向下空间的 1.5 倍）
      
      const MIN_DOWNWARD_SPACE = 100; // 向下打开的最小空间要求
      const MIN_UPWARD_SPACE = 300; // 向上打开的最小空间要求
      
      if (availableSpaceBelow < MIN_DOWNWARD_SPACE && availableSpaceAbove >= availableSpaceBelow * 2) {
        // 向下空间很小且向上空间明显更大，向上打开
        shouldOpenUpward = true;
      } else if (availableSpaceAbove >= MIN_UPWARD_SPACE && availableSpaceAbove >= availableSpaceBelow * 1.5) {
        // 向上空间足够大且明显大于向下空间，向上打开
        shouldOpenUpward = true;
      } else {
        // 默认向下打开（允许溢出）
        shouldOpenUpward = false;
      }
      menuOpenDirectionRef.current = shouldOpenUpward;
    } else {
      // 滚动时保持原有的打开方向
      shouldOpenUpward = menuOpenDirectionRef.current;
    }
    
    // 计算动态最大高度
    let maxHeight: number;
    if (shouldOpenUpward) {
      // 向上打开：限制在可用空间内，防止溢出容器顶部
      // relativeTop 是输入框顶部相对于容器内容区域顶部的位置（单位：px）
      // 这就是输入框到容器顶部的距离，也就是可用的向上空间
      
      // 菜单定位逻辑：
      // - top = relativeTop - 2（菜单底部距离输入框顶部 2px）
      // - 使用 transform: translateY(-100%)，菜单向上移动自身高度的 100%
      // - 所以菜单底部对齐到 top 位置，菜单顶部在 top - 菜单总高度 的位置
      
      // 菜单总高度组成：
      // - .input-recommend-results 的高度（maxHeight，这是我们控制的）
      // - .input-recommend-dialog 的 border（上下各 1px，共 2px）
      // 所以菜单总高度 = maxHeight + 2px
      
      // 菜单顶部位置 = (relativeTop - 2) - (maxHeight + 2) = relativeTop - maxHeight - 4
      // 要防止溢出容器顶部（位置 >= 0），需要：relativeTop - maxHeight - 4 >= 0
      // 即：maxHeight <= relativeTop - 4
      
      // 为了更安全，预留更多空间，使用：maxHeight <= relativeTop - 6
      // 这样菜单顶部位置 = relativeTop - (relativeTop - 6) - 4 = 2px，距离容器顶部 2px
      const reservedSpace = 6; // 2px（间距）+ 2px（border）+ 2px（安全边距）
      
      // 计算 results 内容区域的最大高度
      // relativeTop 是输入框顶部相对于容器内容区域顶部的位置
      // 但表头是 sticky 的，固定在容器顶部，所以实际可用空间需要减去表头高度
      const actualAvailableSpace = relativeTop - headerHeight; // 实际可用空间（减去表头）
      
      // 菜单定位：
      // - top = relativeTop - 2（菜单底部距离输入框顶部 2px）
      // - transform: translateY(-100%)（菜单向上移动自身高度的 100%）
      // - 所以菜单底部对齐到 top 位置
      
      // 菜单总高度 = maxHeight（results 内容） + 2px（dialog border）
      // 菜单顶部位置 = (relativeTop - 2) - (maxHeight + 2) = relativeTop - maxHeight - 4
      // 要防止溢出表头底部（位置 >= headerHeight），需要：relativeTop - maxHeight - 4 >= headerHeight
      // 即：maxHeight <= relativeTop - headerHeight - 4
      
      // 为了更安全，预留更多空间：maxHeight <= relativeTop - headerHeight - 6
      // 这样菜单顶部位置 = relativeTop - (relativeTop - headerHeight - 6) - 4 = headerHeight + 2px
      let maxResultsHeight = actualAvailableSpace - reservedSpace;
      
      // 如果计算结果为负数或很小，说明空间不足
      if (maxResultsHeight <= 0) {
        // 空间严重不足，使用最小可用空间
        // 必须确保不会溢出表头：maxHeight + 2 <= relativeTop - headerHeight - 2，即 maxHeight <= relativeTop - headerHeight - 4
        maxHeight = Math.max(0, actualAvailableSpace - 4);
      } else {
        // 优先使用可用空间，但不能超过容器高度的 75%
        maxHeight = Math.min(maxResultsHeight, maxMenuHeight);
      }
      
      // 最终安全检查：确保菜单总高度不会超出可用空间（考虑表头）
      // 菜单总高度 = maxHeight + 2px（border）
      // 菜单顶部位置 = relativeTop - 2 - (maxHeight + 2) = relativeTop - maxHeight - 4
      // 必须 >= headerHeight，即 maxHeight <= relativeTop - headerHeight - 4
      const maxAllowedHeight = actualAvailableSpace - 4; // 最大允许高度（包括 border，考虑表头）
      if (maxHeight > maxAllowedHeight) {
        // 如果计算出的高度仍然会溢出，强制限制
        maxHeight = Math.max(0, maxAllowedHeight);
      }
    } else {
      // 向下打开：允许溢出，但限制最大高度为容器高度的 75%
      maxHeight = maxMenuHeight;
    }
    
    if (shouldOpenUpward) {
      // 向上打开：菜单底部对齐到输入框顶部，距离2px
      // 使用 top 定位，然后通过 transform 向上移动菜单
      // 菜单顶部位置 = 输入框顶部位置 - 2px（先定位到输入框顶部上方2px）
      // 然后使用 transform: translateY(-100%) 将菜单向上移动其自身高度
      const top = relativeTop - 2;
      
      setMenuPosition({
        top,
        left: relativeLeft,
        width: cellWidth,
        openUpward: true,
        maxHeight,
      });
    } else {
      // 向下打开：菜单顶部对齐到输入框底部，距离2px
      // top 是相对于容器内容区域顶部的距离
      const top = relativeBottom + 2;
      
      setMenuPosition({
        top,
        left: relativeLeft,
        width: cellWidth,
        openUpward: false,
        maxHeight,
      });
    }
  }, [variant, visible]);

  // 计算详情页模式下搜索结果菜单的位置（fixed + portal，避免 overflow 裁切）
  const updateDetailMenuPosition = useCallback((forceRecalculateDirection: boolean = false) => {
    if (variant !== 'detail' || !inputRefInternal.current || !visible) return;

    const inputElement = inputRefInternal.current;
    if (!inputElement.isConnected) return;

    const inputRect = inputElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const availableSpaceAbove = inputRect.top;
    const availableSpaceBelow = viewportHeight - inputRect.bottom;
    const maxMenuHeight = viewportHeight * 0.75;
    const reservedSpace = 6;

    let shouldOpenUpward: boolean;
    if (forceRecalculateDirection || menuOpenDirectionRef.current === null) {
      const MIN_DOWNWARD_SPACE = 100;
      const MIN_UPWARD_SPACE = 300;

      if (availableSpaceBelow < MIN_DOWNWARD_SPACE && availableSpaceAbove >= availableSpaceBelow * 2) {
        shouldOpenUpward = true;
      } else if (availableSpaceAbove >= MIN_UPWARD_SPACE && availableSpaceAbove >= availableSpaceBelow * 1.5) {
        shouldOpenUpward = true;
      } else {
        shouldOpenUpward = false;
      }
      menuOpenDirectionRef.current = shouldOpenUpward;
    } else {
      shouldOpenUpward = menuOpenDirectionRef.current;
    }

    let maxHeight: number;
    if (shouldOpenUpward) {
      maxHeight = Math.min(Math.max(0, availableSpaceAbove - reservedSpace), maxMenuHeight);
    } else {
      maxHeight = Math.min(Math.max(0, availableSpaceBelow - reservedSpace), maxMenuHeight);
    }

    if (shouldOpenUpward) {
      setMenuPosition({
        top: inputRect.top - 2,
        left: inputRect.left,
        width: inputRect.width,
        openUpward: true,
        maxHeight,
      });
    } else {
      setMenuPosition({
        top: inputRect.bottom + 2,
        left: inputRect.left,
        width: inputRect.width,
        openUpward: false,
        maxHeight,
      });
    }
  }, [variant, visible]);

  // 详情页模式：监听输入框的 focus/blur 事件
  useEffect(() => {
    if (variant !== 'detail' || !inputRefInternal.current || !inputElementSet) {
      return;
    }

    const currentInput = inputRefInternal.current;
    
    const handleFocus = () => {
      setVisible(true);
      setSearchText(currentInput.value || '');
    };
    
    const handleBlur = () => {
      // 延迟关闭，给选择推荐项的时间
      setTimeout(() => {
        if (document.activeElement !== currentInput) {
          setVisible(false);
        }
      }, 150);
    };
    
    currentInput.addEventListener('focus', handleFocus);
    currentInput.addEventListener('blur', handleBlur);
    
    // 初始检查
    if (document.activeElement === currentInput) {
      setVisible(true);
      setSearchText(currentInput.value || '');
    }

    return () => {
      currentInput.removeEventListener('focus', handleFocus);
      currentInput.removeEventListener('blur', handleBlur);
    };
  }, [inputElementSet, variant, sceneId, fieldType]);

  // 当 visible 变化时，更新菜单位置
  useEffect(() => {
    if (!visible) {
      menuOpenDirectionRef.current = null;
      return;
    }

    if (variant === 'table') {
      menuOpenDirectionRef.current = null;
      const rafId = requestAnimationFrame(() => {
        updateMenuPosition(true);
      });
      return () => cancelAnimationFrame(rafId);
    }

    if (variant === 'detail') {
      menuOpenDirectionRef.current = null;
      const rafId = requestAnimationFrame(() => {
        updateDetailMenuPosition(true);
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [variant, visible, updateMenuPosition, updateDetailMenuPosition]);

  // 监听滚动和 resize，更新菜单位置（保持打开方向不变）
  useEffect(() => {
    if (!visible || (variant !== 'table' && variant !== 'detail')) return;

    let rafId: number | null = null;

    const handleUpdate = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        if (variant === 'table') {
          updateMenuPosition(false);
        } else {
          updateDetailMenuPosition(false);
        }
        rafId = null;
      });
    };

    const mainTableContainer = document.querySelector('.breakdown-table-container');
    const excelTableContainer = mainTableContainer?.querySelector('.excel-table-container') as HTMLElement;

    if (mainTableContainer) {
      mainTableContainer.addEventListener('scroll', handleUpdate, { passive: true });
    }
    if (excelTableContainer) {
      excelTableContainer.addEventListener('scroll', handleUpdate, { passive: true });
    }

    let windowScrollTimer: ReturnType<typeof setTimeout> | null = null;
    const handleWindowEvents = () => {
      if (windowScrollTimer) clearTimeout(windowScrollTimer);
      windowScrollTimer = setTimeout(() => {
        handleUpdate();
      }, 4);
    };

    window.addEventListener('scroll', handleWindowEvents, { passive: true, capture: true });
    window.addEventListener('resize', handleWindowEvents);
    document.addEventListener('scroll', handleWindowEvents, { passive: true, capture: true });

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      if (windowScrollTimer) clearTimeout(windowScrollTimer);
      if (mainTableContainer) {
        mainTableContainer.removeEventListener('scroll', handleUpdate);
      }
      const currentExcelTableContainer = mainTableContainer?.querySelector('.excel-table-container') as HTMLElement;
      if (currentExcelTableContainer) {
        currentExcelTableContainer.removeEventListener('scroll', handleUpdate);
      }
      window.removeEventListener('scroll', handleWindowEvents, true);
      window.removeEventListener('resize', handleWindowEvents);
      document.removeEventListener('scroll', handleWindowEvents, true);
    };
  }, [variant, visible, updateMenuPosition, updateDetailMenuPosition]);

  // 搜索结果计算：按名称排序，显示全部结果
  const results = useMemo(() => {
    const rawResults = buildResults(dataSource, searchText);
    const normalizedSearch = normalize(searchText)?.toLowerCase();
    // 过滤掉与当前输入完全匹配的结果和"待定-"前缀的结果
    const filteredResults = rawResults.filter(result => {
      if (normalizedSearch && result.toLowerCase() === normalizedSearch) {
        return false;
      }
      if (result.startsWith('暂时去掉待定-')) {
        return false;
      }
      return true;
    });
    return filteredResults;
  }, [dataSource, searchText]);

  // 处理选择结果
  const handleSelect = useCallback(async (text: string) => {
    const inputElement =
      (variant === 'table' && targetInputRef?.current) ||
      inputRefInternal.current;

    const oldValue = inputElement?.value ?? '';
    
    console.log(`[输入推荐选择] sceneID: ${sceneId}, 字段: ${fieldType}, 原值: ${oldValue}, 新值: ${text}`);
    
    if (onSelectValue) {
      onSelectValue(text);
    } else if (inputElement) {
      inputElement.value = text;
    }
    
    // 表格模式下，立即保存值到 ref（确保值被保存）
    if (variant === 'table' && onSaveValue) {
      onSaveValue(text);
    }
    
    // 未走受控回调时，触发 input 事件以便详情页等同步
    if (!onSelectValue && inputElement) {
      const inputEvent = new Event('input', { bubbles: true });
      inputElement.dispatchEvent(inputEvent);
    }
    
    // 场景字段完成输入时，检查并自动填充拍摄地点
    if (fieldType === 'sceneSet' && onAutoFillLocation) {
      const trimmedText = text.trim();
      if (trimmedText) {
        // 设置标记，表明是用户主动选择，避免 MutationObserver 重复调用
        isUserSelectionRef.current = true;
        onAutoFillLocation(sceneId, trimmedText);
      }
    }
    
    // 通过 updateHandlersMap 触发更新（用于详情页）
    if (inputElement) {
      const updateHandler = updateHandlersMap.get(inputElement);
      if (updateHandler) {
        await updateHandler(text);
      }
    }
    
    // 关闭菜单和清理状态
    setSearchText('');
    setVisible(false);
    setSelectedIndex(-1);
    
    // 表格模式下，选择推荐项后完成编辑
    if (variant === 'table') {
      // 如果有 onFinishEdit 回调，直接调用（更可靠）
      if (onFinishEdit) {
        // 使用双重 requestAnimationFrame 确保状态更新完成后再调用
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            onFinishEdit();
          });
        });
      } else {
        // 否则，触发 blur 事件
        // 使用 setTimeout 确保 mousedown 事件处理完成后再触发 blur
        setTimeout(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              // 确保输入框仍然存在且聚焦
              if (inputElement && document.activeElement === inputElement) {
                inputElement.blur();
              } else if (inputElement) {
                // 如果输入框不在焦点，直接触发 blur 事件
                const blurEvent = new FocusEvent('blur', { bubbles: true });
                inputElement.dispatchEvent(blurEvent);
              }
            });
          });
        }, 0);
      }
    }
  }, [
    sceneId,
    fieldType,
    onAutoFillLocation,
    variant,
    onFinishEdit,
    onSaveValue,
    onSelectValue,
    targetInputRef,
  ]);

  // 键盘事件处理：上下方向键导航和回车键选择
  useEffect(() => {
    if (!visible || results.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'Enter') {
        const activeElement = document.activeElement;
        const isInputFocused = activeElement && (
          activeElement.tagName === 'INPUT' || 
          activeElement.tagName === 'TEXTAREA'
        ) && (
          activeElement.getAttribute('data-field') === 'sceneSet' ||
          activeElement.getAttribute('data-field') === 'sceneLocation' ||
          activeElement.closest('.scene-detail-input')
        );

        if (!isInputFocused) return;

        if (e.key === 'ArrowUp') {
          e.preventDefault();
          e.stopPropagation();
          setSelectedIndex(prev => {
            if (prev <= 0) {
              return results.length - 1;
            }
            return prev - 1;
          });
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          e.stopPropagation();
          setSelectedIndex(prev => {
            if (prev < 0) {
              return 0;
            } else if (prev >= results.length - 1) {
              return 0;
            }
            return prev + 1;
          });
        } else if (e.key === 'Enter') {
          if (selectedIndex >= 0 && selectedIndex < results.length) {
            e.preventDefault();
            e.stopPropagation();
            handleSelect(results[selectedIndex]);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [visible, results, selectedIndex, handleSelect]);

  // 滚动到选中项
  useEffect(() => {
    if (selectedIndex >= 0 && resultsRef.current[selectedIndex]) {
      resultsRef.current[selectedIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [selectedIndex]);

  // 点击外部区域关闭
  useEffect(() => {
    if (!visible) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      const isInputField = target.tagName === 'INPUT' && (
        target.getAttribute('data-field') === 'sceneSet' ||
        target.getAttribute('data-field') === 'sceneLocation'
      );
      
      const isInInputContainer = target.closest('.scene-detail-input-container') !== null;
      const isInTableCellWrapper = target.closest('.breakdown-editable-cell-wrapper') !== null;
      
      if (isInputField || isInInputContainer || isInTableCellWrapper) {
        return;
      }
      
      if (containerRef.current && containerRef.current.contains(target)) {
        return;
      }
      
      setVisible(false);
      setSelectedIndex(-1);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [visible]);

  // 当 visible 或 results 变化时，重置选中索引
  useEffect(() => {
    if (visible && results.length > 0) {
      setSelectedIndex(-1);
    } else {
      setSelectedIndex(-1);
    }
  }, [visible, results.length]);

  // 详情页模式的搜索结果菜单（Portal + fixed，避免父级 overflow 裁切）
  const renderDetailMenu = () => {
    if (variant !== 'detail' || !visible || results.length === 0 || !menuPosition) return null;
    if (menuPosition.top === undefined) return null;

    return createPortal(
      <div
        className={`input-recommend input-recommend-${variant} input-recommend-fixed ${menuPosition.openUpward ? 'input-recommend-upward' : ''}`}
        ref={containerRef}
        style={{
          position: 'fixed',
          top: `${menuPosition.top}px`,
          left: `${menuPosition.left}px`,
          width: `${menuPosition.width}px`,
          ...(menuPosition.openUpward ? { transform: 'translateY(-100%)' } : {}),
          zIndex: 10050,
        }}
      >
        <div className="input-recommend-dialog">
          <div
            className="input-recommend-results"
            style={{
              maxHeight: menuPosition.maxHeight ? `${menuPosition.maxHeight}px` : undefined,
            }}
          >
            {results.map((text, index) => (
              <div
                key={text}
                ref={(el) => {
                  if (el) {
                    resultsRef.current[index] = el;
                  }
                }}
                className={`input-recommend-result-item ${selectedIndex === index ? 'input-recommend-result-item-selected' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSelect(text);
                }}
              >
                <div className="input-recommend-info">
                  <div className="input-recommend-title">
                    <span className="input-recommend-name">{text}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // 表格模式的搜索结果菜单（使用 Portal 渲染到表格容器）
  const renderTableMenu = () => {
    if (variant !== 'table' || !visible || results.length === 0 || !menuPosition) return null;
    
    const tableContainer = document.querySelector('.breakdown-table-container');
    if (!tableContainer) return null;
    
    // 确保位置值存在
    if (menuPosition.top === undefined) return null;
    
    return createPortal(
      <div 
        className={`input-recommend input-recommend-${variant} ${menuPosition.openUpward ? 'input-recommend-upward' : ''}`} 
        ref={containerRef}
        style={{
          position: 'absolute',
          top: `${menuPosition.top}px`,
          left: `${menuPosition.left}px`,
          width: `${menuPosition.width}px`,
          ...(menuPosition.openUpward 
            ? { transform: 'translateY(-100%)' }
            : {}
          ),
          zIndex: 60
        }}
      >
        <div className="input-recommend-dialog">
          <div 
            className="input-recommend-results"
            style={{
              maxHeight: menuPosition.maxHeight ? `${menuPosition.maxHeight}px` : undefined
            }}
          >
            {results.map((text, index) => (
              <div
                key={text}
                ref={(el) => {
                  if (el) {
                    resultsRef.current[index] = el;
                  }
                }}
                className={`input-recommend-result-item ${selectedIndex === index ? 'input-recommend-result-item-selected' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.preventDefault(); // 阻止默认行为，防止输入框失去焦点
                  e.stopPropagation();
                  // 在 mousedown 中直接处理选择，确保在 blur 之前执行
                  handleSelect(text);
                }}
              >
                <div className="input-recommend-info">
                  <div className="input-recommend-title">
                    <span className="input-recommend-name">{text}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>,
      tableContainer
    );
  };

  return (
    <>
      {renderDetailMenu()}
      {renderTableMenu()}
    </>
  );
};

export default InputRecommendNEW;
